import { z } from 'zod'

/**
 * Agent browser (PLAN-083). One `BrowserSession` Durable Object per chat
 * thread drives a Cloudflare Browser Run session that the agent and the user
 * share: the agent acts through its tools, the user watches and acts through
 * Cloudflare Live View (an interactive view of the same tab embedded in the
 * workspace panel). The socket `/ws/:projectId/browser/:threadId` only
 * carries state (who is driving, the Live View URL, a pending hand-off).
 */

/** Default tab size; the panel may ask for another while it is expanded. */
export const BROWSER_VIEWPORT = { width: 1440, height: 900 } as const
export const BROWSER_VIEWPORT_MIN = { width: 320, height: 240 } as const
export const BROWSER_VIEWPORT_MAX = { width: 2560, height: 1600 } as const

export const browserViewportSchema = z.object({
  width: z.number().int().min(BROWSER_VIEWPORT_MIN.width).max(BROWSER_VIEWPORT_MAX.width),
  height: z.number().int().min(BROWSER_VIEWPORT_MIN.height).max(BROWSER_VIEWPORT_MAX.height),
})
export type BrowserViewport = z.infer<typeof browserViewportSchema>

export const browserStatusSchema = z.enum(['idle', 'starting', 'ready', 'closed', 'error'])
export type BrowserStatus = z.infer<typeof browserStatusSchema>

export const browserControlSchema = z.enum(['agent', 'user'])
export type BrowserControl = z.infer<typeof browserControlSchema>

export const browserStateSchema = z.object({
  status: browserStatusSchema,
  /** Advisory: the agent's tools refuse while the user is driving. */
  control: browserControlSchema,
  url: z.string().nullable(),
  title: z.string().nullable(),
  /** Set while the agent waits for the user to finish something (Cloudflare hand-off). */
  handoff: z
    .object({
      reason: z.string(),
      since: z.string(),
    })
    .nullable(),
  /** Interactive Live View of the tab (Cloudflare hosted UI); expires, refreshed on demand. */
  liveView: z
    .object({
      url: z.string(),
      expiresAt: z.string(),
    })
    .nullable(),
  /** Current tab size (BROWSER_VIEWPORT unless the panel asked for another). */
  viewport: browserViewportSchema,
  /** Set when status is `error`. */
  error: z.string().nullable(),
  /** ISO time of the last browser action (agent or user). */
  lastActivityAt: z.string().nullable(),
})
export type BrowserState = z.infer<typeof browserStateSchema>

// ── Server → client ───────────────────────────────────────────────────────

export const browserServerMessageSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('state'), state: browserStateSchema }),
  z.object({ t: z.literal('error'), message: z.string() }),
])
export type BrowserServerMessage = z.infer<typeof browserServerMessageSchema>

// ── Client → server ───────────────────────────────────────────────────────

export const browserClientMessageSchema = z.discriminatedUnion('t', [
  /** The user wants to drive: the agent's tools pause until `handBack`. */
  z.object({ t: z.literal('takeControl') }),
  /** Done (or gave up) — completes a pending hand-off and lets the agent continue. */
  z.object({ t: z.literal('handBack'), success: z.boolean().optional() }),
  /** Start the browser (if needed) and (re)issue a Live View URL. */
  z.object({ t: z.literal('open') }),
  /** Resize the tab (expanded panel → the user's own screen size; collapsed → default). */
  z.object({ t: z.literal('viewport'), viewport: browserViewportSchema }),
  z.object({ t: z.literal('close') }),
])
export type BrowserClientMessage = z.infer<typeof browserClientMessageSchema>

/** Tool names the SPA treats as "the browser is in use" (opens the panel). */
export const BROWSER_TOOL_NAMES = new Set([
  'browserNavigate',
  'browserRead',
  'browserClick',
  'browserType',
  'browserPress',
  'browserScroll',
  'browserHandToUser',
  'browserClose',
])
